import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkle, Plus } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const CATEGORIES = [
  'Housing', 'Transportation', 'Food', 'Utilities', 'Healthcare',
  'Credit Cards', 'Debt Payments', 'Software/Subscriptions',
  'Entertainment', 'Shopping', 'Personal', 'Education', 'Other'
];

const BudgetTracker = () => {
  const [comparison, setComparison] = useState([]);
  const [formData, setFormData] = useState({ category: '', monthly_budget: '' });

  useEffect(() => { fetchComparison(); }, []);

  const fetchComparison = async () => {
    try {
      const response = await api.get('/budgets/comparison');
      setComparison(response.data.comparison || []);
    } catch (error) {
      toast.error('Failed to load budgets');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/budgets', {
        category: formData.category,
        monthly_budget: parseFloat(formData.monthly_budget)
      });
      toast.success('Budget saved!');
      setFormData({ category: '', monthly_budget: '' });
      fetchComparison();
    } catch (error) {
      toast.error('Failed to save budget');
    }
  };

  const totalBudgeted = comparison.reduce((sum, c) => sum + c.budgeted, 0);
  const totalActual = comparison.reduce((sum, c) => sum + c.actual, 0);
  const totalVariance = totalBudgeted - totalActual;

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Tool:</strong> Compare monthly budget targets to actual spending. Set realistic budgets based on your financial goals.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">Budget vs Actual</h1>
        <p className="text-base text-[#4A5568]">Set monthly budgets and track spending against them</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Total Budget</p>
          <p className="text-3xl font-semibold text-[#0A1128] ibm-plex">${totalBudgeted.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Actual Spent</p>
          <p className="text-3xl font-semibold text-[#C5A059] ibm-plex">${totalActual.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Variance</p>
          <p className={`text-3xl font-semibold ibm-plex ${totalVariance >= 0 ? 'text-[#2D6A4F]' : 'text-[#C53030]'}`}>
            ${Math.abs(totalVariance).toLocaleString()}
          </p>
          <p className="text-xs text-[#4A5568] mt-1">{totalVariance >= 0 ? 'Under Budget' : 'Over Budget'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Set Budget</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                  <SelectTrigger data-testid="budget-category-select" className="border-slate-300">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="monthly_budget">Monthly Budget ($)</Label>
                <Input id="monthly_budget" type="number" step="0.01" data-testid="budget-amount-input"
                  value={formData.monthly_budget} onChange={(e) => setFormData({ ...formData, monthly_budget: e.target.value })}
                  className="border-slate-300" required />
              </div>
              <Button type="submit" data-testid="save-budget-button" className="w-full bg-[#0A1128] hover:bg-[#152145]">
                <Plus size={18} className="mr-2" /> Save Budget
              </Button>
              <p className="text-xs text-[#4A5568]">Adding a category will replace the existing budget for that category.</p>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="budget-comparison-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Budget vs Actual</h2>
            {comparison.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={comparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="category" stroke="#4A5568" style={{ fontSize: '11px' }} />
                  <YAxis stroke="#4A5568" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="budgeted" name="Budgeted" fill="#0A1128" />
                  <Bar dataKey="actual" name="Actual" fill="#C5A059" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-[#4A5568] py-16">Add budgets to see comparison</p>
            )}
          </div>
        </div>
      </div>

      {comparison.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Detailed Breakdown</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Category</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Budgeted</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Actual</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Variance</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">% Used</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100" data-testid={`budget-row-${item.category}`}>
                    <td className="py-3 px-4 text-sm font-medium">{item.category}</td>
                    <td className="py-3 px-4 text-sm text-right ibm-plex">${item.budgeted.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-right ibm-plex">${item.actual.toLocaleString()}</td>
                    <td className={`py-3 px-4 text-sm text-right ibm-plex font-medium ${item.variance >= 0 ? 'text-[#2D6A4F]' : 'text-[#C53030]'}`}>
                      {item.variance >= 0 ? '+' : ''}${item.variance.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-sm text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 bg-slate-200 rounded-full h-2 overflow-hidden">
                          <div className={`h-full rounded-full ${item.percentage_used <= 100 ? 'bg-[#C5A059]' : 'bg-[#C53030]'}`}
                            style={{ width: `${Math.min(item.percentage_used, 100)}%` }} />
                        </div>
                        <span className="text-xs font-medium ibm-plex w-12 text-right">{item.percentage_used.toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetTracker;
